import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Megaphone,
  Plus,
  Search,
  Filter,
  CheckCircle2,
  Clock,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  useSafetyCommunications,
  type CommunicationFilters,
} from '@/hooks/use-safety-communications';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { SafetyCommunication, CommunicationCategory } from '@/lib/types';

const CATEGORY_LABELS: Record<CommunicationCategory, string> = {
  irata_bulletin: 'IRATA Bulletin',
  manufacturer_notice: 'Manufacturer Notice',
  internal_memo: 'Internal Memo',
  toolbox_update: 'Toolbox Update',
  other: 'Other',
};

function AckBadge({ comm }: { comm: SafetyCommunication }) {
  if (!comm.requires_acknowledgment) {
    return (
      <Badge variant="secondary" className="flex items-center gap-1">
        No Ack Required
      </Badge>
    );
  }
  const count = comm.acknowledgement_count;
  return (
    <Badge
      variant={count > 0 ? 'default' : 'outline'}
      className="flex items-center gap-1"
    >
      {count > 0 ? (
        <CheckCircle2 className="h-3 w-3" />
      ) : (
        <Clock className="h-3 w-3" />
      )}
      {count} acknowledged
    </Badge>
  );
}

function CommunicationRow({ comm }: { comm: SafetyCommunication }) {
  const timeZone = useOrgTimeZone();
  return (
    <Link
      to={`/compliance/communications/${comm.id}`}
      className="block p-4 border rounded-lg hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-gray-400 shrink-0" />
            <h3 className="font-medium text-sm truncate">{comm.title}</h3>
          </div>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
            <span>{comm.category_display}</span>
            <span>{comm.target_audience_display}</span>
            {comm.sent_by_username && <span>By: {comm.sent_by_username}</span>}
            <span>{formatDateTimeInTimeZone(comm.sent_at, timeZone, { dateStyle: 'medium' })}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <Badge variant="secondary">{comm.category_display}</Badge>
          <AckBadge comm={comm} />
        </div>
      </div>
    </Link>
  );
}

export function CommunicationsPage() {
  const [filters, setFilters] = useState<CommunicationFilters>({
    page: 1,
    page_size: 25,
  });
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading, error } = useSafetyCommunications(filters);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, search: searchInput, page: 1 }));
  };

  const communications = data?.results || [];
  const totalCount = data?.count || 0;

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600">Failed to load communications. Please try again later.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Safety Communications</h1>
          <p className="text-gray-600 mt-1">
            IRATA bulletins, manufacturer notices, and internal safety memos (QP-300 Clauses 3.3.1–3.3.3)
          </p>
        </div>
        <Button asChild>
          <Link to="/compliance/communications/new">
            <Plus className="h-4 w-4 mr-2" />
            New Communication
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search communications..."
                  className="pl-9"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <Button type="submit" variant="outline" size="icon">
                <Filter className="h-4 w-4" />
              </Button>
            </form>
            <Select
              value={filters.category || 'all'}
              onValueChange={(v) =>
                setFilters((prev) => ({ ...prev, category: v === 'all' ? undefined : v, page: 1 }))
              }
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.ack_status || 'all'}
              onValueChange={(v) =>
                setFilters((prev) => ({ ...prev, ack_status: v === 'all' ? undefined : v, page: 1 }))
              }
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Ack Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Communications List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Communications</span>
            <span className="text-sm font-normal text-gray-500">{totalCount} total</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : communications.length === 0 ? (
            <div className="text-center py-12">
              <Megaphone className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No safety communications found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {communications.map((comm) => (
                <CommunicationRow key={comm.id} comm={comm} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalCount > (filters.page_size || 25) && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                disabled={!data?.previous}
                onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-500">
                Page {filters.page || 1} of {Math.ceil(totalCount / (filters.page_size || 25))}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!data?.next}
                onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
